import React, { useEffect, useState } from "react"

import { IDAM } from "../../api/Registry"
import HttpServices from "../../services/HttpServices"
import { IdentityPhase } from "./wizard/IdentityPhase"
import { generateUniqueId } from "../../utils/Helpers"
import { PhaseBridge } from "../../lib/phases/PhaseBridge"
import { DynamicPhaseHandler } from "./wizard/DynamicPhaseHandler"
import { PhaseManifest, PhaseState } from "../../utils/Interfaces"
import { PhaseErrorState } from "../../lib/phases/PhaseErrorState"
import { SplitScreenModal } from "../../lib/fragments/SplitScreenModal"
import { getPhaseBucket, lookupElement, modifyElement } from "../../lib/phases/PhaseHelper"
import { G_onInputBlurHandler, G_onInputChangeHandler } from "../../components/utils/InputHandlers"

export const CreateManagedAccount = ({ show, showOrHide, onComplete }) => {
    const [state, setstate] = useState({
        posting: false,
        httpStatus: 200,
        status: 'pending',
        data: {
            providers: null,
            attributes: null,
        },
        input: [],
        faults: [],
    })

    const onCloseModal = async () => {
        showOrHide()

        // Reset the state on close
        setTimeout(() => {
            setstate((prev: any) => ({
                ...prev,
                posting: false,
                httpStatus: 200,
                status: 'pending',
                data: {
                    group: null
                },
                input: [],
                faults: [],
            }))
        }, 300)
    }

    const [phaseState, setPhaseState] = useState<PhaseState>({
        manifest: [],
        current: null,
        previous: null,
        next: null
    });

    const initializePhasing = (phases: PhaseManifest[], activeSlug: string = 'pm_identity_access') => {
        // Ensure the manifest is sorted by sequence
        const sortedManifest = [...phases].sort((a, b) => a.sequence - b.sequence);

        // Locate the current phase index
        const currentIndex = sortedManifest.findIndex(p => p.slug === activeSlug);
        const current = sortedManifest[currentIndex] || sortedManifest[0];

        // Resolve Previous/Next references
        const prevPhase = sortedManifest[currentIndex - 1];
        const nextPhase = sortedManifest[currentIndex + 1];

        return {
            manifest: sortedManifest,
            current,
            previous: prevPhase ? { sequence: prevPhase.sequence, slug: prevPhase.slug } : null,
            next: nextPhase ? { sequence: nextPhase.sequence, slug: nextPhase.slug } : null,
        };
    };

    useEffect(() => {
        if (show) {
            fetchManagedAccountPrerequistes()
        }
    }, [show])

    const fetchManagedAccountPrerequistes = async () => {
        let { status, httpStatus, data } = state

        // Generate initial input & error buckets
        // This creates the { [slug]: {} } structure for every phase
        const initialInput: any = [];
        const initialFaults: any = [];

        try {
            const [providerRes, attributeRes]: any[] = await Promise.all([
                HttpServices.httpGet(IDAM.PROVIDERS_IDX),
                HttpServices.httpGet(IDAM.ATTR_DEFINITIONS)
            ]);

            if (providerRes.status === 200 && attributeRes.status === 200) {
                const providers = providerRes.data.payload.providers;
                const attributes = attributeRes.data.payload.attr;

                const staticPhaseManifest: PhaseManifest[] = [
                    {
                        gid: generateUniqueId('pm_identity_access'),
                        sequence: 0,
                        slug: 'pm_identity_access',
                        title: 'Account Essentials',
                        caption: 'Set up the user’s profile and preferred authentication method',
                        module: 'static',
                    }
                ]

                // Generate sequence since index from below starts at 0
                let phaseSequence = staticPhaseManifest.length

                // Initialize Static Phase bucket (pm_identity_access)
                initialInput.push({
                    'pm_identity_access': {
                        auth: providers[0],
                        email: '',
                        first_name: '',
                        last_name: ''
                    }
                });
                initialFaults.push({
                    'pm_identity_access': {
                        email: '',
                        first_name: '',
                        last_name: ''
                    }
                });

                const dynamicPhaseManifest: PhaseManifest[] = attributes.map((attr: any, index: number) => {
                    const phaseTitle = attr.e
                    const phaseSlug = `pm_${phaseTitle.toLowerCase().replace(/\s+/g, '_')}`;

                    // Initialize the buckets for each specific slug
                    const phaseInputs = {}
                    const phaseErrors = {}

                    // Loop through the elements (attr.s) to extract input IDs
                    // Assume 'g' is the input object containing the ID
                    attr.s.forEach((element: any) => {
                        // Resolve Default Values
                        const elementType = element.f;
                        let defaultValue: any;

                        switch (elementType.format) {
                            case 'number': defaultValue = 0; break;
                            case 'boolean': defaultValue = false; break;
                            case 'predefined':
                                defaultValue = elementType?.allow_multiple ? [] : null;
                                break;
                            default: defaultValue = '';
                        }

                        // Set the default value for each input ID
                        phaseInputs[element.g] = defaultValue;
                        phaseErrors[element.g] = '';
                    });

                    // PUSH the keyed object into the state arrays
                    initialInput.push({ [phaseSlug]: phaseInputs });
                    initialFaults.push({ [phaseSlug]: phaseErrors });

                    return ({
                        gid: attr.g,
                        sequence: phaseSequence + index,
                        slug: phaseSlug,
                        title: phaseTitle,
                        caption: attr.t,
                        module: 'dynamic',
                    })
                });

                // Initialize the Phase State Engine
                const phaseManifestMap = staticPhaseManifest.concat(dynamicPhaseManifest)
                const newPhaseState = initializePhasing(phaseManifestMap, 'pm_identity_access');

                setPhaseState(newPhaseState)
                console.log('initialInput', initialInput);


                // Update the states
                data.providers = providers;
                data.attributes = attributes;

                httpStatus = 200
                status = 'fulfilled'
            } else {
                status = 'rejected'
            }
        } catch (error) {
            httpStatus = 500
            status = 'rejected'
            console.log(error);
        }

        setstate((prevState: any) => ({
            ...prevState,
            status,
            httpStatus,
            input: initialInput,
            faults: initialFaults,
            data: {
                ...prevState.data,
                ...data,
            },
        }));
    }

    /**
     * Serves as the viewport for resolving the PhaseManifest module type and renders either a 
     * static component or a dynamic component.
     * 
     * @param {string} currentPhase - The unique identifier for the phase (e.g., 'pm_identity_access').
     */
    const phaseProjection = (currentPhase: any) => {
        if (!currentPhase) return null
        let { input, faults, data } = state

        // Call the helper to get the "active" bucket
        const currentValues = getPhaseBucket(currentPhase.slug, input);
        const currentFaults = getPhaseBucket(currentPhase.slug, faults);

        switch (currentPhase.module) {
            /**
             * Dynamic modules utilize the FormComposer engine to render inputs 
             * based on the 'axt' and 'glyph' metadata provided by the server.
             */
            case 'dynamic':
                return <DynamicPhaseHandler
                    state={state}
                    phase={currentPhase}
                    values={currentValues}
                    faults={currentFaults}
                    blurHandler={onInputBlur}
                    changeHandler={onChangeHandler}
                    listboxHandler={onListBoxHandler}
                />

            /**
             * Static modules are pre-defined components defined within the application 
             * bundle (e.g., Identity, Success screens). They are resolved via slug.
             */
            case 'static':
                switch (currentPhase.slug) {
                    case 'pm_identity_access':
                        // Phase 1: Only needs provider selection
                        return <IdentityPhase
                            state={state}
                            phase={currentPhase}
                            values={currentValues}
                            faults={currentFaults}
                            blurHandler={onInputBlur}
                            changeHandler={onChangeHandler}
                            listboxHandler={onListBoxHandler}
                        />

                    default:
                        console.error(`Error: No render engine found for slug "${currentPhase.title}"`);

                        return <PhaseErrorState
                            message={`Error: No render engine found for slug "${currentPhase.title}"`}
                        />
                }

            default:
                return null

        }
    };

    const goToPhaseHandler = (goToSlug: any) => {
        const { posting } = state
        const { manifest } = phaseState

        if (posting) return

        const phaseIndex = manifest.findIndex(p => p.slug === goToSlug);
        const current = manifest[phaseIndex] || manifest[0];

        // Resolve Previous/Next references
        const prevPhase = manifest[phaseIndex - 1];
        const nextPhase = manifest[phaseIndex + 1];

        setPhaseState((prevPhaseState: any) => ({
            ...prevPhaseState,
            current,
            previous: prevPhase ? { sequence: prevPhase.sequence, slug: prevPhase.slug } : null,
            next: nextPhase ? { sequence: nextPhase.sequence, slug: nextPhase.slug } : null
        }))
    }

    const onChangeHandler = (e: any) => {
        let { current } = phaseState
        let { posting, input } = state

        if (posting) return

        const phaseSlug = current.slug
        const { name, value } = e.target;

        let currentValue = lookupElement(name, phaseSlug, input);
        let output: any = G_onInputChangeHandler(e)

        if (typeof currentValue === "number") {
            output.value = value === "" || isNaN(Number(value)) ? 0 : Number(value);
        }

        setstate(prev => ({
            ...prev,
            input: modifyElement(name, phaseSlug, output.value, prev.input),
            faults: modifyElement(name, phaseSlug, output.error, prev.faults)
        }));
    }

    const onInputBlur = (e: any, currentPhase: any, element: any = null) => {
        let { posting, input, data } = state
        if (posting) return

        const phaseSlug = currentPhase.slug
        const { name, value } = e.target;

        let output: any
        let currentValue = lookupElement(name, phaseSlug, input);

        switch (currentPhase.module) {
            case 'static':
                output = G_onInputBlurHandler(e, '')

                if (typeof currentValue === "number") {
                    if (isNaN(value) || value === "") {
                        output.error = "Enter a valid number. Use 0 for empty values.";
                    } else {
                        // Ensures conversion of the value into an number
                        output.value = Number(value)
                        output.error = ''
                    }
                }
                break;

            case 'dynamic':
                // TODO: get the title of the form element
                console.log('element', element);

                output = G_onInputBlurHandler(e, element.e, element.f?.minimum, element.f?.maximum)

                if (element.f?.format == "number") {
                    if (isNaN(value) || value === "") {
                        output.error = "Enter a valid number. Use 0 for empty values.";
                    } else {
                        // Ensures conversion of the value into an number
                        output.value = Number(value)
                        output.error = ''
                    }
                }
                break;
        }

        setstate(prev => ({
            ...prev,
            input: modifyElement(name, phaseSlug, output.value, prev.input),
            faults: modifyElement(name, phaseSlug, output.error, prev.faults)
        }));
    }

    /**
     * Handles state updates for checkboxes and radio buttons in the `input` array.
     * - Checkboxes: Allows multiple selections, adding/removing values dynamically.
     * - Radio Buttons: Ensures only one value is selected per group.
     *
     * @param {Event} e - The input change event.
     * @param {string} oid - The unique section identifier.
     */
    const onOptionChange = (e: any, slug: string, xid: string) => {
        const { posting } = state;
        if (posting) return;

        const { type, value, name } = e.target;

        setstate((prev) => ({
            ...prev,
            // input: modifyElement(prev.input, slug, xid, value, type === "checkbox"),
            // faults: modifyElement(prev.faults, slug, name, ""), // Clear faults if any
        }));
    };

    const onListBoxHandler = (e: any, xid: string, currentPhase: any) => {
        let { posting, input, data } = state
        if (posting) return

        const phaseSlug = currentPhase.slug

        setstate(prev => ({
            ...prev,
            input: modifyElement(xid, phaseSlug, e, prev.input),
            faults: modifyElement(xid, phaseSlug, '', prev.faults)
        }));
    }

    const previousPhaseHandler = () => {
        const { previous } = phaseState;
        const { posting } = state

        if (posting) return

        if (previous) {
            goToPhaseHandler(previous.slug);
        }
    }

    const nextPhaseHandler = async (e: any) => {
        e.preventDefault()

        let { input, posting } = state
        const { current, next, manifest } = phaseState;

        if (posting || !current) return

        let hasValidationError = false

        switch (current.module) {
            // Utility that checks the formData against the current phase requirements
            case 'static':
                switch (current.slug) {
                    case 'pm_identity_access':
                        // Validate that auth provider has been set
                        const authProvider = lookupElement('auth', current.slug, input)

                        if (!authProvider) {
                            setstate((prev: any) => ({
                                ...prev,
                                faults: modifyElement('auth', current.slug, 'Authentication provider required', prev.faults)
                            }));

                            return
                        }

                        const fieldsToValidate = [
                            'email',
                            'first_name',
                            'last_name',
                        ]

                        fieldsToValidate.forEach((validatableKey: string) => {
                            try {
                                // Capture the specific DOM element from the form using the key
                                const formElement = e.target[validatableKey];

                                // Construct a synthetic event object to satisfy the onInputBlur requirements
                                const eventContext: any = {
                                    target: {
                                        name: formElement.name,
                                        required: formElement.required,
                                        value: formElement.value.trim(),
                                    }
                                };

                                const handlerResult: any = G_onInputBlurHandler(eventContext, '');

                                if (handlerResult.error) {
                                    // Invalidate the process
                                    hasValidationError = true

                                    setstate(prev => ({
                                        ...prev,
                                        faults: modifyElement(validatableKey, current.slug, handlerResult.error, prev.faults)
                                    }));
                                }
                            } catch (err) {
                                // Silently catch errors for missing elements or undefined properties during bulk validation
                            }
                        });
                        break
                }
                break

            case 'dynamic':

                break
        }

        if (hasValidationError) {
            // toast.error("Please complete all required fields.");
            return;
        }

        // 1. Run Validation Logic
        // You can have a utility that checks the formData against the current phase requirements
        // const isValid = validatePhase(current.slug, formData);

        // if (!isValid) {
        //     toast.error("Please complete all required fields.");
        //     return;
        // }

        // 2. Branch: Move Next OR Submit Final
        if (next) {
            /**
             * We are not at the end. 
             * Logic here: Update the URL or State to the next slug.
             */
            goToPhaseHandler(next.slug);
        } else {
            /**
             * No 'next' phase exists in the registry.
             * Logic here: Trigger the final API submission.
             */
            // performFinalSubmit(formData);
        }
    }

    return (
        <React.Fragment>
            <SplitScreenModal
                show={show}
                status={state.status}
                posting={state.posting}
                showOrHide={onCloseModal}
                formHandler={nextPhaseHandler}
                formAction={
                    phaseState.next === null ? "Create" : "Next"
                }
                phase={({
                    prevHandler: previousPhaseHandler,
                    prev: phaseState.previous,
                    next: phaseState.next
                })}
                header={
                    <>
                        <span className="block text-xl md:text-2xl">
                            Create Managed Account

                            <span className="text-sm pt-4 pb-2 text-stone-600 block">
                                Create an account for someone in your organization.
                            </span>
                        </span>

                        <PhaseBridge
                            phaseState={phaseState}
                        />
                    </>
                }
                contents={
                    state.status === 'fulfilled' && (
                        <>
                            <div className="w-full flex flex-col md:px-3 px-1.5 flex-1">
                                {
                                    phaseProjection(phaseState.current)
                                }
                            </div>
                        </>
                    )
                }
            />
        </React.Fragment>
    )
}